import { useEffect, useContext, useRef } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { useLatest } from 'react-use';
import _ from 'lodash';
import { MapContext } from '../contexts/MapContext';
import { mapModes, measurementModes } from '../constants/map';
import { handleBackspaceWhileEditing } from '../utils/draw';
import { acresPerSqMeter } from '../constants/units';

const mapboxDrawModes = {
  [measurementModes.AREA]: 'draw_polygon',
  [measurementModes.DISTANCE]: 'draw_line_string',
};

export default (map) => {
  const {
    mapState: { mapMode, context, drawRef, setMapMode },
  } = useContext(MapContext);

  const setMapModeRef = useLatest(setMapMode);

  const { measurementMode = measurementModes.DISTANCE } = context;

  const featureCreatedRef = useRef(false);

  useEffect(() => {
    if (mapMode !== mapModes.MEASUREMENT) return undefined;
    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      defaultMode: mapboxDrawModes[measurementMode],
    });
    const handleModeChange = () => {
      if (featureCreatedRef.current) {
        // Start over
        featureCreatedRef.current = false;
        drawRef.current.deleteAll();
        drawRef.current.changeMode(mapboxDrawModes[measurementMode]);
        // Reset the measurement value (distance and area)
        setMapModeRef.current(mapModes.MEASUREMENT, null, {
          measurementMode,
        });
      } else {
        // Select the newly created feature
        const { features } = drawRef.current.getAll();
        drawRef.current.changeMode('direct_select', {
          featureId: features[0].id,
        });
        featureCreatedRef.current = true;
      }
    };
    const updateResult = _.throttle(() => {
      const features = map.getStyle().sources['mapbox-gl-draw-hot']?.data
        .features;

      if (features && features.length > 0) {
        if (measurementMode === measurementModes.DISTANCE) {
          const feature = features.find(
            (f) => f.geometry.type === 'LineString',
          );
          if (!feature) return;

          setMapModeRef.current(mapModes.MEASUREMENT, null, {
            measurementMode,
            distance: turf.length(feature, { units: 'miles' }),
          });
        } else if (measurementMode === measurementModes.AREA) {
          const feature = features.find((f) => f.geometry.type === 'Polygon');
          if (!feature) return;

          setMapModeRef.current(mapModes.MEASUREMENT, null, {
            measurementMode,
            area: turf.area(feature) * acresPerSqMeter,
          });
        }
      }
    }, 50);

    const handleKeyDown = (e) => {
      if (drawRef.current.getMode() === 'direct_select') {
        handleBackspaceWhileEditing(e, drawRef.current);
        updateResult();
      }
    };

    map.addControl(drawRef.current);

    map.on('draw.create', updateResult);
    map.on('draw.update', updateResult);
    map.on('draw.modechange', handleModeChange);
    map.on('mousemove', updateResult);
    map.getCanvas().addEventListener('keydown', handleKeyDown);

    return () => {
      map.removeControl(drawRef.current);
      map.off('draw.create', updateResult);
      map.off('draw.update', updateResult);
      map.off('draw.modechange', handleModeChange);
      map.off('mousemove', updateResult);
      map.getCanvas().removeEventListener('keydown', handleKeyDown);
      drawRef.current = null;
      featureCreatedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode, measurementMode]);
};
